import { useThreadShells } from "~/state/entities";
import { useWorkspaceRevisionRefresh } from "./useWorkspaceRevisionRefresh";
/**
 * The Files panel's "Other conversations" section: the person's files across
 * their other conversations in this organization, as the server's workspace
 * catalog reports them. Pure model here, React in `OtherConversationsFiles`.
 */
import type {
  EnvironmentId,
  ThreadId,
  WorkspaceCatalogListResult,
  WorkspaceCatalogThread,
} from "@amoreg/contracts";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import { useCallback, useEffect, useState } from "react";

import { workspaceCatalog } from "~/state/projects";
import { useEnvironmentQuery } from "~/state/query";
import type { RightPanelFileOrigin } from "~/rightPanelStore";

/** The section's search box drives this query; keep the wire quiet while typing. */
export const CATALOG_QUERY_DEBOUNCE_MS = 200;
/** Files shown per request; the section says when there are more. */
export const CATALOG_FILE_LIMIT = 200;

export interface CatalogConversation {
  readonly threadId: ThreadId;
  readonly title: string;
  readonly href: string;
  readonly updatedAt: string;
  readonly fileCount: number;
  readonly files: WorkspaceCatalogThread["files"];
  readonly listingTruncated: boolean;
  /**
   * Where the files can be opened from. `null` when this client holds no
   * shell for the conversation (it is not in the org snapshot), in which
   * case the row can only link to the conversation.
   */
  readonly origin: RightPanelFileOrigin | null;
}

export interface CatalogSectionModel {
  readonly conversations: ReadonlyArray<CatalogConversation>;
  readonly conversationCount: number;
  readonly fileCount: number;
  readonly truncated: boolean;
}

const untitled = (title: string, threadId: string): string =>
  title.trim().length > 0 ? title : `Conversation ${threadId.slice(0, 8)}`;

/**
 * Join the catalog with the thread shells this client already holds: the
 * shell's `worktreePath` is the directory the file RPCs need, and it is the
 * same value the server used to list the files, so an origin built from it
 * can only ever name that conversation's own workspace.
 */
export const buildCatalogSectionModel = (
  result: WorkspaceCatalogListResult | null,
  shells: ReadonlyArray<Pick<EnvironmentThreadShell, "id" | "environmentId" | "worktreePath">>,
  environmentId: EnvironmentId,
): CatalogSectionModel => {
  if (result === null)
    return { conversations: [], conversationCount: 0, fileCount: 0, truncated: false };
  // Thread ids are unique within an environment, not across them: only
  // this environment's shells may supply a directory.
  const cwdByThread = new Map<string, string>();
  for (const shell of shells) {
    if (shell.environmentId !== environmentId) continue;
    if (shell.worktreePath !== null) cwdByThread.set(shell.id, shell.worktreePath);
  }
  return {
    conversations: result.threads.map((thread) => {
      const title = untitled(thread.title, thread.threadId);
      const cwd = cwdByThread.get(thread.threadId);
      return {
        threadId: thread.threadId,
        title,
        href: thread.href,
        updatedAt: thread.updatedAt,
        fileCount: thread.fileCount,
        files: thread.files,
        listingTruncated: thread.listingTruncated,
        origin: cwd === undefined ? null : { threadId: thread.threadId, cwd, title },
      };
    }),
    conversationCount: result.threadCount,
    fileCount: result.fileCount,
    truncated: result.truncated,
  };
};

/** The section's search text as a catalog query: trimmed, bounded, blank means everything. */
export const catalogQueryFromSearch = (search: string): string | undefined => {
  const trimmed = search.trim().slice(0, 256);
  return trimmed.length === 0 ? undefined : trimmed;
};

const useDebounced = <A>(value: A, delayMs: number): A => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
};

export interface WorkspaceCatalogTarget {
  readonly environmentId: EnvironmentId;
  readonly organizationId: string;
  /** The conversation whose panel is asking; its own files are already on screen. */
  readonly excludeThreadId: ThreadId;
  readonly search: string;
  /** Collapsed sections do not query at all. */
  readonly enabled: boolean;
}

export function useWorkspaceCatalogQuery(target: WorkspaceCatalogTarget) {
  const query = catalogQueryFromSearch(target.search);
  const debouncedQuery = useDebounced(query, CATALOG_QUERY_DEBOUNCE_MS);
  const result = useEnvironmentQuery(
    target.enabled
      ? workspaceCatalog({
          environmentId: target.environmentId,
          input: {
            organizationId: target.organizationId,
            excludeThreadId: target.excludeThreadId,
            fileLimit: CATALOG_FILE_LIMIT,
            ...(debouncedQuery === undefined ? {} : { query: debouncedQuery }),
          },
        })
      : null,
  );
  const refresh = result.refresh;
  const shells = useThreadShells();
  // Include every owned shell in this organization, including conversations
  // absent from an empty/filtered catalog. Their first file must appear too.
  const revision = catalogWorkspaceRevision(shells, target);
  useWorkspaceRevisionRefresh(target.enabled ? revision : undefined, refresh);
  return {
    data: result.data,
    error: result.error,
    isPending: target.enabled && (query !== debouncedQuery || result.isPending),
    refresh: useCallback(() => refresh(), [refresh]),
  };
}

/** Stable under chat activity and sidebar sorting; scoped before aggregation. */
export function catalogWorkspaceRevision(
  shells: ReadonlyArray<
    Pick<EnvironmentThreadShell, "id" | "environmentId" | "projectId" | "workspaceRevision">
  >,
  target: Pick<WorkspaceCatalogTarget, "environmentId" | "organizationId" | "excludeThreadId">,
): string {
  return JSON.stringify(
    shells
      .filter(
        (thread) =>
          thread.environmentId === target.environmentId &&
          thread.projectId === target.organizationId &&
          thread.id !== target.excludeThreadId,
      )
      .map((thread) => [thread.id, thread.workspaceRevision ?? null] as const)
      .sort(([left], [right]) => left.localeCompare(right)),
  );
}
